import { useState, useEffect, useCallback, useRef } from 'react'

import { useChatSse } from '../../../sse'
import type { ChatMessageResponse } from '../types'

interface UseAutoScrollChatOptions {
	roomId: string
	messages: ChatMessageResponse[]
	messagesLoading: boolean
	isFetched: boolean
	startCursor: string | null
	hasPreviousPage: boolean
	resetToLatest: () => void
}

export function useAutoScrollChat({
	roomId,
	messages,
	messagesLoading,
	isFetched,
	startCursor,
	hasPreviousPage,
	resetToLatest,
}: UseAutoScrollChatOptions) {
	const { onNewMessage } = useChatSse()
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const messagesContainerRef = useRef<HTMLDivElement>(null)
	const isNearBottomRef = useRef(true)
	const hasInitialScrollRef = useRef(false)
	const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	const [showScrollToBottom, setShowScrollToBottom] = useState(false)
	const [newMessageCount, setNewMessageCount] = useState(0)
	const [lastNewMessagePreview, setLastNewMessagePreview] = useState<
		string | undefined
	>()
	const [highlightedMessageId, setHighlightedMessageId] = useState<
		string | null
	>(null)

	// Reset state when room changes
	useEffect(() => {
		setNewMessageCount(0)
		setShowScrollToBottom(false)
		setLastNewMessagePreview(undefined)
		setHighlightedMessageId(null)
		hasInitialScrollRef.current = false
		isNearBottomRef.current = true

		return () => {
			if (highlightTimeoutRef.current) {
				clearTimeout(highlightTimeoutRef.current)
			}
		}
	}, [roomId])

	// Reset initial scroll flag when isFetched becomes false (e.g., after resetToLatest)
	// This allows the initial scroll effect to run again after refetch
	useEffect(() => {
		if (!isFetched) {
			hasInitialScrollRef.current = false
		}
	}, [isFetched])

	// Check if user is near bottom of chat
	const checkIfNearBottom = useCallback(() => {
		const container = messagesContainerRef.current
		if (!container) return true

		const threshold = 150 // pixels from bottom
		const isNear =
			container.scrollHeight - container.scrollTop - container.clientHeight <
			threshold
		isNearBottomRef.current = isNear
		return isNear
	}, [])

	// Update scroll indicator on scroll
	const updateScrollIndicator = useCallback(() => {
		const isNear = checkIfNearBottom()
		setShowScrollToBottom(!isNear)

		// Reset new message count when scrolled to bottom
		if (isNear) {
			setNewMessageCount(0)
		}
	}, [checkIfNearBottom])

	// Scroll to bottom function
	// If we're not at the latest page (hasPreviousPage), reset to latest first
	const scrollToBottom = useCallback(
		(behavior: ScrollBehavior = 'smooth') => {
			// If we're viewing older messages (not at latest page), reset to latest
			if (hasPreviousPage) {
				resetToLatest()
				// After reset, the initial scroll effect will scroll to bottom
				return
			}

			const container = messagesContainerRef.current
			if (!container) return

			// Use scrollTop for more reliable scrolling
			const scrollTo = container.scrollHeight - container.clientHeight

			if (behavior === 'instant') {
				container.scrollTop = scrollTo
			} else {
				container.scrollTo({
					top: scrollTo,
					behavior: 'smooth',
				})
			}

			setShowScrollToBottom(false)
			setNewMessageCount(0)
			setLastNewMessagePreview(undefined)
			isNearBottomRef.current = true
		},
		[hasPreviousPage, resetToLatest],
	)

	// Force scroll to bottom (for when admin sends message)
	const forceScrollToBottom = useCallback(() => {
		// Use requestAnimationFrame to ensure DOM is updated
		requestAnimationFrame(() => {
			const container = messagesContainerRef.current
			if (!container) return
			container.scrollTop = container.scrollHeight - container.clientHeight
			isNearBottomRef.current = true
			setShowScrollToBottom(false)
			setNewMessageCount(0)
			setLastNewMessagePreview(undefined)
		})
	}, [])

	// Scroll to a specific message element and highlight it
	const scrollToMessageAndHighlight = useCallback(
		(messageId: string, behavior: ScrollBehavior = 'instant') => {
			const container = messagesContainerRef.current
			if (!container) return false

			const messageElement = container.querySelector(
				`[data-message-id="${messageId}"]`,
			)
			if (!messageElement) return false

			// Scroll the message into view
			messageElement.scrollIntoView({
				behavior,
				block: 'center',
			})

			// Clear any existing timeout
			if (highlightTimeoutRef.current) {
				clearTimeout(highlightTimeoutRef.current)
			}

			// Wait for scroll to complete, then highlight (shake)
			setTimeout(
				() => {
					setHighlightedMessageId(messageId)
					// Clear highlight after animation completes
					highlightTimeoutRef.current = setTimeout(() => {
						setHighlightedMessageId(null)
					}, 600)
				},
				behavior === 'instant' ? 50 : 300,
			)

			isNearBottomRef.current = false
			return true
		},
		[],
	)

	// Scroll to existing message (already in messages array)
	const scrollToExistingMessage = useCallback(
		(messageId: string) => {
			const messageExists = messages.some((msg) => msg.id === messageId)
			if (!messageExists) return false

			requestAnimationFrame(() => {
				scrollToMessageAndHighlight(messageId)
			})
			return true
		},
		[messages, scrollToMessageAndHighlight],
	)

	// Initial scroll after first fetch
	// Priority: 1. startCursor (from context) -> 2. unread divider -> 3. scroll to bottom
	useEffect(() => {
		// Wait for fetch to complete
		if (messagesLoading || !isFetched || hasInitialScrollRef.current) return
		if (messages.length === 0) return

		// Mark as handled to prevent re-triggering
		hasInitialScrollRef.current = true

		requestAnimationFrame(() => {
			// 1. If has startCursor (from context), scroll to that message and shake
			if (startCursor) {
				const messageExists = messages.some((msg) => msg.id === startCursor)
				if (messageExists) {
					setTimeout(() => {
						scrollToMessageAndHighlight(startCursor, 'instant')
					}, 50) // Small delay to ensure DOM is ready
					return
				}
			}

			// 2. If has unread divider, scroll to it
			const unreadDividerId = `unread-divider-${roomId}`
			const unreadDivider = document.querySelector(
				`[data-message-id="${unreadDividerId}"]`,
			)

			if (unreadDivider) {
				unreadDivider.scrollIntoView({ behavior: 'instant', block: 'start' })
				isNearBottomRef.current = false
				return
			}

			// 3. Default: scroll to bottom
			scrollToBottom('instant')
		})
	}, [
		roomId,
		messages,
		messagesLoading,
		isFetched,
		startCursor,
		scrollToBottom,
		scrollToMessageAndHighlight,
	])

	// Listen for new incoming messages via SSE
	useEffect(() => {
		const unsubscribe = onNewMessage((payload) => {
			// Only handle messages for this room
			if (payload.roomId !== roomId) return

			// Only count incoming messages (from customer, not admin)
			if (payload.message.direction !== 'INCOMING') return

			// If near bottom, scroll to see the full new message
			if (isNearBottomRef.current) {
				// Use requestAnimationFrame to ensure DOM is updated with the new message
				requestAnimationFrame(() => {
					const container = messagesContainerRef.current
					if (!container) return
					container.scrollTo({
						top: container.scrollHeight - container.clientHeight,
						behavior: 'smooth',
					})
				})
				return
			}

			// If not near bottom, increment new message count and show preview
			setNewMessageCount((prev) => prev + 1)
			setLastNewMessagePreview(payload.message.text ?? undefined)
		})

		return unsubscribe
	}, [onNewMessage, roomId])

	return {
		messagesEndRef,
		messagesContainerRef,
		showScrollToBottom,
		newMessageCount,
		lastNewMessagePreview,
		highlightedMessageId,
		scrollToBottom,
		forceScrollToBottom,
		updateScrollIndicator,
		scrollToExistingMessage,
		isNearBottom: isNearBottomRef.current,
	}
}
